import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Trash2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/layout/Header";
import { Cta } from "@/components/Cta";
import { useSeo } from "@/lib/seo";

const STATUSES = ["new", "in_review", "contacted", "archived"];
const LABELS = { name: "Name", founder_name: "Founder", email: "Email", company: "Company", project_name: "Project", service: "Service", stage: "Stage", partnership_type: "Partnership", stage_budget: "Stage / budget", preferred_contact: "Preferred contact", description: "Description", idea: "Idea", problem: "Problem", target_users: "Target users", team: "Team", links: "Links", source_page: "Source page" };

export function AdminLogin() {
  useSeo("Admin sign in", "", { noindex: true });
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/auth/login", { email, password }); nav("/admin"); }
    catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-si-bg px-5">
      <form onSubmit={submit} className="panel w-full max-w-sm space-y-5 p-8" data-testid="admin-login-form">
        <Logo className="h-9" />
        <h1 className="text-2xl font-bold">Admin sign in</h1>
        <div><label htmlFor="ae" className="field-label">Email</label><input id="ae" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" /></div>
        <div><label htmlFor="ap" className="field-label">Password</label><input id="ap" type="password" required className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" /></div>
        {error && <p role="alert" className="text-sm text-[#ff8f8f]" data-testid="admin-login-error">{error}</p>}
        <Cta type="submit" disabled={busy} className="w-full" testId="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</Cta>
      </form>
    </div>
  );
}

function Detail({ item, onStatus, onDelete }) {
  const d = item?.data || {};
  return (
    <div className="panel p-6" data-testid="admin-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="eyebrow">{item.kind === "contact" ? "Project inquiry" : "Partnership application"}</span>
        <span className="font-mono text-xs text-si-muted">{new Date(item.created_at).toLocaleString()}</span>
      </div>
      <h2 className="mt-4 text-2xl font-bold">{d.name || d.founder_name}</h2>
      <a href={`mailto:${d.email}`} className="mt-1 inline-flex items-center gap-2 text-si-cyan"><Mail className="h-4 w-4" />{d.email}</a>
      <dl className="mt-6 space-y-4">
        {Object.entries(LABELS).filter(([k]) => d[k] && !["name", "founder_name", "email"].includes(k)).map(([k, l]) => (
          <div key={k}><dt className="text-xs font-semibold uppercase tracking-wider text-si-muted">{l}</dt><dd className="mt-1 whitespace-pre-wrap text-[15px]">{String(d[k])}</dd></div>
        ))}
      </dl>
      <p className="mt-6 text-xs text-si-muted">Email notification: {item.email_status}</p>
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-si-line pt-5">
        <select value={item.status} onChange={(e) => onStatus(item.id, e.target.value)} className="field !w-auto" data-testid="admin-status-select">
          {STATUSES.map((s) => <option key={s} value={s} className="bg-si-bg">{s.replace("_", " ")}</option>)}
        </select>
        <button type="button" onClick={() => onDelete(item.id)} className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 px-4 py-3 text-sm text-[#ffb0b0] hover:bg-red-400/10" data-testid="admin-delete"><Trash2 className="h-4 w-4" />Delete</button>
      </div>
    </div>
  );
}

export function AdminInbox() {
  useSeo("Admin inbox", "", { noindex: true });
  const nav = useNavigate();
  const [kind, setKind] = useState("contact");
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/submissions", { params: { kind } });
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
      setSel((s) => list.find((x) => x.id === s?.id) || list[0] || null);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
      else toast.error(formatApiError(err));
    }
  }, [kind, nav]);
  useEffect(() => { load(); }, [load]);
  const onStatus = async (id, status) => {
    try { await api.patch(`/admin/submissions/${id}`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try { await api.delete(`/admin/submissions/${id}`); toast.success("Deleted"); setSel(null); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); nav("/admin/login"); };
  return (
    <div className="min-h-screen bg-si-bg" data-testid="admin-inbox">
      <header className="border-b border-si-line">
        <div className="wrap flex h-16 items-center justify-between">
          <div className="flex items-center gap-4"><Logo className="h-8" /><span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-si-muted sm:inline">Inbox</span></div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-si-muted hover:text-si-text" data-testid="admin-logout"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>
      <div className="wrap py-8">
        <div className="flex flex-wrap items-center gap-2">
          {[["contact", "Project inquiries"], ["partnership", "Partnership applications"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setKind(k); setItems(null); }} data-testid={`admin-tab-${k}`}
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${kind === k ? "border-si-cyan bg-si-cyan text-si-bg" : "border-si-line text-si-muted"}`}>{l}</button>
          ))}
          <button type="button" onClick={load} aria-label="Refresh" className="ml-auto grid h-10 w-10 place-items-center rounded-lg border border-si-line" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
        {items === null ? (
          <div className="grid h-60 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-si-cyan" /></div>
        ) : items.length === 0 ? (
          <p className="mt-16 text-center text-si-muted" data-testid="admin-empty">No submissions yet.</p>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-12">
            <ul className="space-y-2 lg:col-span-5" data-testid="admin-list">
              {items.map((it) => (
                <li key={it.id}>
                  <button type="button" onClick={() => setSel(it)} data-testid={`admin-item-${it.id}`}
                    className={`w-full rounded-xl border p-4 text-left transition-colors ${sel?.id === it.id ? "border-si-cyan bg-si-surface" : "border-si-line hover:bg-si-surface/60"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold">{it.data?.name || it.data?.founder_name}</span>
                      <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase ${it.status === "new" ? "bg-si-cyan/15 text-si-cyan" : "bg-si-line text-si-muted"}`}>{it.status.replace("_", " ")}</span>
                    </div>
                    <p className="mt-1 truncate text-sm text-si-muted">{it.data?.service || it.data?.project_name} · {new Date(it.created_at).toLocaleDateString()}</p>
                  </button>
                </li>
              ))}
            </ul>
            <div className="lg:col-span-7">{sel && <Detail item={sel} onStatus={onStatus} onDelete={onDelete} />}</div>
          </div>
        )}
      </div>
    </div>
  );
}
